<template>
  <div id="contenedor-tablero-expedientes">
    <v-container fluid>
      <v-card flat class="mb-4 rounded-lg">
        <v-card-text class="px-0 py-1">
          <TituloToolBar titulo="Tablero de Expedientes">
          </TituloToolBar>
          <ToolBar
            :filtros="filtros"
            :datosCompletos="datosCompletos"
            :resultados="resultadosFiltrados"
            @limpiar-filtros="limpiarFiltros">
          </ToolBar>
        </v-card-text>
      </v-card>

      <v-alert v-if="!rangoValido" type="warning" outlined class="mb-4">
        El rango de fechas es inválido: la fecha final debe ser mayor o igual a la fecha inicial.
      </v-alert>

      <template v-else>
        <v-skeleton-loader
          v-if="$apollo.loading"
          type="card, table-heading, table-thead, table-tbody">
        </v-skeleton-loader>

        <v-alert v-else-if="esErrorAutorizacion" type="error" outlined class="mb-4">
          No tiene permiso para consultar el Tablero de Expedientes.
        </v-alert>

        <v-alert v-else-if="errorConsulta" type="error" outlined class="mb-4">
          Ocurrió un error al consultar el tablero de expedientes. Intente nuevamente.
        </v-alert>

        <template v-else>
          <Tarjetas :resultados="resultadosFiltrados"></Tarjetas>

          <v-card flat>
            <v-card-text>
              <v-alert
                v-if="resultadosFiltrados.length === 0"
                type="info"
                outlined>
                No se encontraron contratos con los filtros seleccionados.
              </v-alert>
              <ListadoDesktop v-else :resultados="resultadosFiltrados"></ListadoDesktop>
            </v-card-text>
          </v-card>
        </template>
      </template>
    </v-container>
  </div>
</template>

<script>
import TituloToolBar from '@/components/TituloToolBar.vue';
import TABLERO_EXPEDIENTES from '@/graphql/querys/administracion/tableroExpedientes/tableroExpedientes.gql';
import ToolBar from './ToolBar.vue';
import Tarjetas from './Tarjetas.vue';
import ListadoDesktop from './listado/Desktop.vue';
import {
  sanitizarFiltrosDesdeQuery, limpiarFiltrosParaQuery, sanearFiltrosContraDatos,
} from './filtrosUtils';

const CODIGOS_ERROR_AUTORIZACION = ['UNAUTHENTICATED', 'FORBIDDEN'];

const filtrosPorDefecto = () => ({
  fechaInicio: '', // se completa en created()
  fechaFin: '',
  buscarTexto: '',
  contratista: null,
  responsable: null,
  estatus: null,
  estadoExpediente: null,
});

export default {
  name: 'TableroExpedientesDefault',
  components: {
    TituloToolBar,
    ToolBar,
    Tarjetas,
    ListadoDesktop,
  },
  data() {
    return {
      filtros: filtrosPorDefecto(),
      errorConsulta: null,
      tableroExpedientes: null,
    };
  },
  apollo: {
    tableroExpedientes: {
      query: TABLERO_EXPEDIENTES,
      variables() {
        return {
          filtros: {
            fechaInicio: this.filtros.fechaInicio,
            fechaFin: this.filtros.fechaFin,
          },
        };
      },
      skip() {
        return !this.rangoValido;
      },
      result(result) {
        if (!result.loading) {
          this.errorConsulta = result.error || null;
        }
      },
      error(error) {
        this.errorConsulta = error;
      },
    },
  },
  created() {
    const filtrosBase = {
      ...filtrosPorDefecto(),
      fechaInicio: this.$moment().startOf('year').format('YYYY-MM-DD'),
      fechaFin: this.$moment().format('YYYY-MM-DD'),
    };
    this.filtros = sanitizarFiltrosDesdeQuery(this.$route.query, filtrosBase);
  },
  methods: {
    limpiarFiltros() {
      this.filtros = {
        ...filtrosPorDefecto(),
        fechaInicio: this.$moment().startOf('year').format('YYYY-MM-DD'),
        fechaFin: this.$moment().format('YYYY-MM-DD'),
      };
    },
  },
  computed: {
    rangoValido() {
      if (!this.filtros.fechaInicio || !this.filtros.fechaFin) return false;
      return this.$moment(this.filtros.fechaFin).isSameOrAfter(this.filtros.fechaInicio);
    },
    esErrorAutorizacion() {
      if (!this.errorConsulta || !this.errorConsulta.graphQLErrors) return false;
      return this.errorConsulta.graphQLErrors.some(
        (error) => CODIGOS_ERROR_AUTORIZACION.includes(error.extensions && error.extensions.code),
      );
    },
    datosCompletos() {
      if (!this.tableroExpedientes) return [];
      return this.tableroExpedientes;
    },
    resultadosFiltrados() {
      const texto = (this.filtros.buscarTexto || '').trim().toLowerCase();

      return this.datosCompletos
        .filter((contrato) => {
          if (!texto) return true;
          return contrato.numero.toLowerCase().includes(texto)
            || contrato.meta.toLowerCase().includes(texto)
            || (contrato.contratista
              && contrato.contratista.nombreRazonSocial.toLowerCase().includes(texto));
        })
        .filter((contrato) => (
          !this.filtros.contratista || contrato.contratista.id === this.filtros.contratista
        ))
        .filter((contrato) => {
          if (!this.filtros.responsable) return true;
          return contrato.responsables.some(
            ({ principal, funcionario }) => principal
              && funcionario.id === this.filtros.responsable,
          );
        })
        .filter((contrato) => (
          !this.filtros.estatus
          || (contrato.estatus && contrato.estatus.id === this.filtros.estatus)
        ))
        .filter((contrato) => (
          !this.filtros.estadoExpediente
          || contrato.estadoExpediente === this.filtros.estadoExpediente
        ))
        .slice()
        .sort((a, b) => {
          const aTieneFaltantes = a.faltantes > 0 ? 0 : 1;
          const bTieneFaltantes = b.faltantes > 0 ? 0 : 1;
          if (aTieneFaltantes !== bTieneFaltantes) return aTieneFaltantes - bTieneFaltantes;

          const aPorcentaje = a.porcentajeIntegracion === null ? -1 : a.porcentajeIntegracion;
          const bPorcentaje = b.porcentajeIntegracion === null ? -1 : b.porcentajeIntegracion;
          if (aPorcentaje !== bPorcentaje) return aPorcentaje - bPorcentaje;

          return new Date(b.fecha) - new Date(a.fecha);
        });
    },
  },
  watch: {
    filtros: {
      deep: true,
      handler(filtros) {
        this.$router.replace({ query: limpiarFiltrosParaQuery(filtros) }).catch(() => {});
      },
    },
    datosCompletos(datosCompletos) {
      this.filtros = sanearFiltrosContraDatos(this.filtros, datosCompletos);
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
